/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { MutableRefObject } from 'react';

import logger from 'js/app/loggerSingleton';

import { breakpoints } from '@coursera/cds-core';

import HorizontalSpacing from 'bundles/browse-common/components/HorizontalSpacing';
import BrowseFAQsModuleRenderer from 'bundles/browse-common/renderers/BrowseFAQsModuleRenderer';
import AnyCollectionRenderer from 'bundles/browse-common/renderers/ComplexCollectionRenderer/AnyCollectionRenderer';
import DegreeCollectionRenderer from 'bundles/browse-common/renderers/DegreeCollectionRenderer';
import ExploreRolesRenderer from 'bundles/browse-common/renderers/ExploreRolesRenderer';
import LoggedOutProgressiveOnboardingRenderer from 'bundles/browse-common/renderers/LoggedOutProgressiveOnboardingRenderer';
import RelatedRolesRenderer from 'bundles/browse-common/renderers/RelatedRolesRenderer';
import { SearchFrameRenderer } from 'bundles/browse-common/renderers/SearchFrameRenderer';
import TopPartnersComponentRenderer from 'bundles/browse-common/renderers/TopPartnersComponentRenderer';
import TrackedCampaignCannedCollectionModuleRenderer from 'bundles/browse-common/renderers/TrackedCampaignCannedCollectionModuleRenderer';
import ValuePropositionRenderer from 'bundles/browse-common/renderers/ValuePropositionRenderer';
import PageConfigComponentRendererWrapper from 'bundles/page-config-common/components/shared/PageConfigComponentRendererWrapper';
import CoachOnboardingBannerModuleRenderer from 'bundles/page-config-common/module-renderers/CoachOnboardingBannerModuleRenderer';
import DomainListRenderer from 'bundles/page-config-common/module-renderers/DomainListRenderer';
import RelatedSkillsRenderer from 'bundles/page-config-common/module-renderers/RelatedSkillsRenderer';
import SpotlightCollectionModuleRenderer from 'bundles/page-config-common/module-renderers/SpotlightCollectionModuleRenderer';
import type { PageConfig_ComponentConfigFragment as PageConfigComponent } from 'bundles/page-config-common/queries/page-config/__generated__/PageConfigComponentsQuery';

type Props = {
  pageConfigComponents: Array<PageConfigComponent>;
};

// Standarize all top margins to 0 and bottom margins to 32px / 24px
const styles = {
  standardMarginBottom: css`
    /* Checking for a heading element as a proxy to see whether the component actually rendered.
     * This is to avoid applying the margin to components that don't render. */
    &:has(h1, h2, h3, h4) {
      margin-bottom: var(--cds-spacing-400);
      ${breakpoints.down('sm')} {
        margin-bottom: var(--cds-spacing-300);
      }
    }
  `,
  domainList: css`
    & > div > div {
      margin-top: 0;
      margin-bottom: 0;
    }
  `,
  valueProposition: css`
    & > .page-config-component-content > div > div {
      margin-top: 0;
      margin-bottom: 0;

      & > div > div {
        & > div:nth-of-type(1) {
          padding-top: 0;
          padding-bottom: 0;
        }

        & > div:nth-of-type(2) {
          padding-top: 0;
          padding-bottom: 0;
        }
      }
    }
  `,
  coachOnboardingBanner: css`
    & > .page-config-component-content > div > div {
      margin: 0;
      width: 100%;

      & > div {
        margin: 0;
      }
    }
  `,
  relatedSkills: css`
    & > div > div {
      margin-top: 0;
      margin-bottom: 0;
    }
  `,
  degreeCollection: css`
    & > .page-config-component-content > div:nth-of-type(2) > div {
      padding-top: 0 !important;
      padding-bottom: 0 !important;

      & > div {
        margin-top: 0;
        margin-bottom: 0;

        & > div:nth-of-type(1) {
          padding-top: 0;
          padding-bottom: 0;

          & > div > div {
            margin-bottom: 0;
          }
        }

        & > div:nth-of-type(2) {
          padding-top: 0;
          padding-bottom: 0;

          & > div {
            margin-top: 0;
          }
        }
      }
    }
  `,
  exploreRoles: css`
    & > .page-config-component-content > section > div {
      margin-top: 0;
      margin-bottom: 0;

      & > div {
        padding-top: 0 !important;
        padding-bottom: 0 !important;
      }
    }
  `,

  searchFrame: css`
    & > .page-config-component-content > section {
      padding-top: 0;
      padding-bottom: 0;

      & > div > div {
        margin-top: 0;
        margin-bottom: 0;

        & > div {
          margin-top: 0;
          margin-bottom: 0;

          & > div:nth-of-type(2) {
            padding-bottom: 0;
          }

          & > div:nth-of-type(3) {
            padding-bottom: 0;
          }
        }
      }
    }
  `,
  topPartners: css`
    & > .page-config-component-content > div > div {
      margin-top: 0;
      margin-bottom: 0;

      & > div > div {
        padding-top: 0;
        padding-bottom: 0;
      }
    }
  `,
  faq: css`
    & > .page-config-component-content > div > div > div:nth-of-type(2) > div {
      padding-top: 0;
      padding-bottom: 0;

      & > div {
        margin-top: 0;
        margin-bottom: 0;

        & > div {
          padding-top: 0;
          padding-bottom: 0;

          & > div {
            margin-top: 0;
            margin-bottom: 0;

            & > div:nth-of-type(1) {
              padding-top: 0;
            }

            & > div:nth-of-type(2) {
              padding-bottom: 0;
            }

            & > div:nth-of-type(3) {
              padding-bottom: 0;
            }
          }
        }
      }
    }
  `,
  loggedOutProgressiveOnboarding: css`
    & > div > div {
      margin-top: 0;
      margin-bottom: 0;
    }
  `,
  spotlightCollection: css`
    /* Fix for simple spotlight collection */
    & .simple-spotlight-collection-module > div {
      padding-top: 0;
      padding-bottom: 0;
    }

    & section {
      padding-top: 0;
      padding-bottom: 0;
    }

    /* Fix for complex spotlight collection */
    & > .page-config-component-content > div > div {
      padding-top: 0;
      padding-bottom: 0;

      & > div > div {
        margin-top: 0;
        margin-bottom: 0;

        & > div {
          padding-top: 0 !important;
          padding-bottom: 0 !important;
        }
      }
    }
  `,
};

function renderComponent(
  pageConfigComponent: PageConfigComponent,
  pageConfigComponents: Array<PageConfigComponent | null | undefined>,
  index: number,
  trackableContentRef?: MutableRefObject<HTMLDivElement | null>
) {
  switch (pageConfigComponent.__typename) {
    case 'PageConfig_BrowseValuePropositionComponent': {
      return (
        <div css={[styles.standardMarginBottom, styles.valueProposition]}>
          <HorizontalSpacing>
            <div className="page-config-component-content" ref={trackableContentRef}>
              <ValuePropositionRenderer />
            </div>
          </HorizontalSpacing>
        </div>
      );
    }
    case 'PageConfig_BrowseDomainListComponent': {
      return (
        <div css={[styles.standardMarginBottom, styles.domainList]}>
          <HorizontalSpacing>
            <div className="page-config-component-content" ref={trackableContentRef}>
              <DomainListRenderer pageConfig={pageConfigComponent} />
            </div>
          </HorizontalSpacing>
        </div>
      );
    }
    case 'PageConfig_BrowseRelatedSkillsComponent': {
      return (
        <div css={[styles.standardMarginBottom, styles.relatedSkills]}>
          <HorizontalSpacing>
            <div className="page-config-component-content" ref={trackableContentRef}>
              <RelatedSkillsRenderer pageConfig={pageConfigComponent} />
            </div>
          </HorizontalSpacing>
        </div>
      );
    }
    case 'PageConfig_BrowseDegreeCollectionComponent': {
      return (
        <div css={[styles.standardMarginBottom, styles.degreeCollection]}>
          <div className="page-config-component-content" ref={trackableContentRef}>
            <DegreeCollectionRenderer />
          </div>
        </div>
      );
    }
    case `PageConfig_CoachOnboardingBanner`: {
      return (
        <div css={[styles.standardMarginBottom, styles.coachOnboardingBanner]}>
          <div className="page-config-component-content" ref={trackableContentRef}>
            <CoachOnboardingBannerModuleRenderer hideWhenOnboardingCompleted={true} />
          </div>
        </div>
      );
    }
    case `PageConfig_BrowseLoggedOutProgressiveOnboardingComponent`: {
      return (
        <div css={[styles.standardMarginBottom, styles.loggedOutProgressiveOnboarding]}>
          <HorizontalSpacing>
            <div className="page-config-component-content" ref={trackableContentRef}>
              <LoggedOutProgressiveOnboardingRenderer />
            </div>
          </HorizontalSpacing>
        </div>
      );
    }
    case 'PageConfig_ExploreRolesComponent': {
      return (
        <div css={[styles.standardMarginBottom, styles.exploreRoles]}>
          <div className="page-config-component-content" ref={trackableContentRef}>
            <ExploreRolesRenderer pageConfigComponent={pageConfigComponent} />
          </div>
        </div>
      );
    }
    case 'PageConfig_RelatedRolesComponent': {
      return (
        <div css={[styles.standardMarginBottom, styles.exploreRoles]}>
          <div className="page-config-component-content" ref={trackableContentRef}>
            <RelatedRolesRenderer pageConfigComponent={pageConfigComponent} />
          </div>
        </div>
      );
    }
    case 'PageConfig_CollectionComponent': {
      return (
        <div className="page-config-component-content" ref={trackableContentRef}>
          <AnyCollectionRenderer pageComponentList={pageConfigComponents} pageConfigComponent={pageConfigComponent} />
        </div>
      );
    }
    case 'PageConfig_TopPartnersComponent': {
      return (
        <div css={[styles.standardMarginBottom, styles.topPartners]}>
          <HorizontalSpacing>
            <div className="page-config-component-content" ref={trackableContentRef}>
              <TopPartnersComponentRenderer />
            </div>
          </HorizontalSpacing>
        </div>
      );
    }
    case 'PageConfig_SearchFrameComponent': {
      return (
        <div css={[styles.standardMarginBottom, styles.searchFrame]}>
          <SearchFrameRenderer />
        </div>
      );
    }
    case 'PageConfig_FAQComponent': {
      return (
        <div css={[styles.standardMarginBottom, styles.faq]}>
          <div className="page-config-component-content" ref={trackableContentRef}>
            <BrowseFAQsModuleRenderer />
          </div>
        </div>
      );
    }
    case 'PageConfig_CampaignsCannedCollectionComponent': {
      return (
        <div className="page-config-component-content" ref={trackableContentRef}>
          <TrackedCampaignCannedCollectionModuleRenderer />
        </div>
      );
    }
    case 'PageConfig_SpotlightCollectionComponent': {
      return (
        <div css={[styles.standardMarginBottom, styles.spotlightCollection]}>
          <div className="page-config-component-content" ref={trackableContentRef}>
            <SpotlightCollectionModuleRenderer
              key={pageConfigComponent.id}
              pageConfigComponent={pageConfigComponent}
              hideCoachButton={true}
              showSingleCardDisplay={true}
            />
          </div>
        </div>
      );
    }
    default: {
      logger.warn('unexpected pageConfigComponent', pageConfigComponent);
      return <div ref={trackableContentRef} />;
    }
  }
}

const BrowsePageConfigComponentRenderer = (props: Props) => {
  const { pageConfigComponents } = props;

  return (
    <PageConfigComponentRendererWrapper pageConfigComponents={pageConfigComponents} renderComponent={renderComponent} />
  );
};

export default BrowsePageConfigComponentRenderer;
